import { h } from 'preact';
import { isVideoFile, getYoutubeId, getYoutubePlaylistId } from 'utils/assets';
import { getYoutubePlaylist } from 'utils/youtube';

/**
 * Returns a thumbnail element for the given asset source. Supports YouTube videos/playlists and direct video/image URLs.
 * @param {*} src The source URL of the asset.
 * @param {*} forceVideo Whether to force the asset to be treated as a video.
 * @param {*} callback A callback function to be called when the thumbnail is ready.
 * @returns A Preact element representing the thumbnail.
 */
export function getAssetThumb(src, forceVideo = false, callback = () => {}) {
	if (!src) return null;
	const youtubeId = getYoutubeId(src);
	if (youtubeId) {
		return <img src={`https://img.youtube.com/vi/${youtubeId}/0.jpg`} alt="" />;
	}
	const youtubePlaylistId = getYoutubePlaylistId(src);
	if (youtubePlaylistId) {
		// Using callback to notify when the thumbnail is ready
		const thumbnailSrc = getYoutubePlaylistThumbnailIfAvailable(youtubePlaylistId, callback);
		return <img src={thumbnailSrc} alt="" />;
	}
	if (forceVideo || isVideoFile(src)) {
		return <video src={src} autoPlay controls loop />;
	}
	return <img src={src} alt="" />;
}

// Gets (and caches) youtube playlist thumbnails, but return a value synchronously
// Until ready, returns a default thumbnail
export const getYoutubePlaylistThumbnailIfAvailable = (() => {
	const defaultPreview = 'https://img.youtube.com/vi/default/0.jpg';
	const cache = new Map();

	const raw = async id => {
		const items = await getYoutubePlaylist(id);
		// available sizes: default, medium, high, standard
		const thumbnail = items?.[0]?.snippet?.thumbnails?.default?.url || defaultPreview;
		cache.set(id, thumbnail);
		return thumbnail;
	};

	return (id, callback) => {
		if (cache.has(id)) {
			const value = cache.get(id);
			if (value instanceof Promise) {
				value.then(callback);
				return defaultPreview;
			}
			return value;
		}
		const promise = raw(id);
		cache.set(id, promise);
		promise.then(callback);
		return defaultPreview;
	};
})();